import type { ModelRetryAdvice, ModelRetryAdviceRequest } from '../../model';

export function getAnthropicRetryAdvice(
  args: ModelRetryAdviceRequest,
): ModelRetryAdvice | undefined {
  const { error } = args;
  const status = getStatusCode(error);
  const message = error instanceof Error ? error.message : undefined;
  const normalizedMessage = message?.toLowerCase();

  if (
    status === 408 ||
    status === 409 ||
    status === 429 ||
    (typeof status === 'number' && status >= 500) ||
    normalizedMessage?.includes('overloaded') ||
    normalizedMessage?.includes('rate limit')
  ) {
    return {
      suggested: true,
      reason: message,
      retryAfterMs: getRetryAfterMs(error),
    };
  }

  if (
    status === 400 ||
    status === 401 ||
    status === 403 ||
    status === 404 ||
    normalizedMessage?.includes('context') ||
    normalizedMessage?.includes('invalid request')
  ) {
    return {
      suggested: false,
      reason: message,
    };
  }

  return undefined;
}

function getStatusCode(error: unknown): number | undefined {
  if (!isRecord(error)) {
    return undefined;
  }
  if (typeof error.status === 'number') {
    return error.status;
  }
  if (typeof error.statusCode === 'number') {
    return error.statusCode;
  }
  return undefined;
}

function getRetryAfterMs(error: unknown): number | undefined {
  const retryAfter = getHeaderValue(error, 'retry-after');
  if (!retryAfter) {
    return undefined;
  }
  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }
  const date = Date.parse(retryAfter);
  if (Number.isFinite(date)) {
    return Math.max(0, date - Date.now());
  }
  return undefined;
}

function getHeaderValue(error: unknown, key: string): string | undefined {
  if (!isRecord(error)) {
    return undefined;
  }

  for (const source of [error.headers, error.responseHeaders]) {
    if (!source) {
      continue;
    }
    if (typeof Headers !== 'undefined' && source instanceof Headers) {
      return source.get(key) ?? undefined;
    }
    if (isRecord(source)) {
      const normalizedKey = key.toLowerCase();
      for (const [headerName, value] of Object.entries(source)) {
        if (
          headerName.toLowerCase() === normalizedKey &&
          typeof value === 'string'
        ) {
          return value;
        }
      }
    }
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
